/**
 * Nickname generation and validation utilities.
 *
 * Format: `<Word1><Word2><Word3>#<HashCode>`
 * - 3 words selected from separate 50-word pools (2-letter words each)
 * - Hash code is a random 4-digit number (0000-9999)
 * - Nicknames can be duplicated; the hash code differentiates users
 * - Nickname can be changed once; hash code is permanent
 */

// Pool A: 50 short words (nature/elements)
const POOL_A = [
  'Go',
  'Hi',
  'No',
  'So',
  'My',
  'To',
  'Do',
  'Up',
  'By',
  'Be',
  'Lo',
  'Ha',
  'Ho',
  'La',
  'Ma',
  'Pa',
  'Ra',
  'Sa',
  'Ta',
  'Wa',
  'Ax',
  'Bo',
  'Da',
  'Fa',
  'Ga',
  'Ja',
  'Ka',
  'Na',
  'Va',
  'Za',
  'Al',
  'El',
  'Il',
  'Ol',
  'Ul',
  'Am',
  'Em',
  'Im',
  'Om',
  'Um',
  'An',
  'En',
  'In',
  'On',
  'Un',
  'Ar',
  'Er',
  'Ir',
  'Or',
  'Ur',
];

// Pool B: 50 short words (action/quality)
const POOL_B = [
  'Ky',
  'Vy',
  'Ty',
  'Ry',
  'Ny',
  'Dy',
  'Fy',
  'Gy',
  'Hy',
  'Jy',
  'Xe',
  'Ze',
  'Ye',
  'We',
  'Ve',
  'Te',
  'Se',
  'Re',
  'Pe',
  'Ne',
  'Li',
  'Mi',
  'Ni',
  'Pi',
  'Ri',
  'Si',
  'Ti',
  'Vi',
  'Wi',
  'Xi',
  'Bu',
  'Cu',
  'Du',
  'Fu',
  'Gu',
  'Hu',
  'Ju',
  'Ku',
  'Lu',
  'Mu',
  'Ba',
  'Ca',
  'Ea',
  'Ia',
  'Oa',
  'Ua',
  'Ya',
  'Zi',
  'Qi',
  'Fi',
];

// Pool C: 50 short words (misc)
const POOL_C = [
  'Ox',
  'Ex',
  'Ix',
  'Ux',
  'Az',
  'Ez',
  'Iz',
  'Oz',
  'Uz',
  'Ab',
  'Eb',
  'Ib',
  'Ob',
  'Ub',
  'Ac',
  'Ec',
  'Ic',
  'Oc',
  'Uc',
  'Ad',
  'Ed',
  'Id',
  'Od',
  'Ud',
  'Af',
  'Ef',
  'If',
  'Of',
  'Uf',
  'Ag',
  'Eg',
  'Ig',
  'Og',
  'Ug',
  'Ah',
  'Eh',
  'Ih',
  'Oh',
  'Uh',
  'Aj',
  'Ej',
  'Ij',
  'Oj',
  'Uj',
  'Ak',
  'Ek',
  'Ik',
  'Ok',
  'Uk',
  'At',
];

/**
 * Generate a random nickname from 3 word pools.
 * Returns a 6-character name like "GoKyOx".
 */
export function generateNickname(): string {
  const w1 = POOL_A[Math.floor(Math.random() * POOL_A.length)];
  const w2 = POOL_B[Math.floor(Math.random() * POOL_B.length)];
  const w3 = POOL_C[Math.floor(Math.random() * POOL_C.length)];
  return `${w1}${w2}${w3}`;
}

/**
 * Generate a random 4-digit hash code (0000-9999).
 */
export function generateHashCode(): string {
  return String(Math.floor(Math.random() * 10000)).padStart(4, '0');
}

/**
 * Format a full display nickname: "Name#0000"
 */
export function formatNickname(nickname: string, hashCode: string): string {
  return `${nickname}#${hashCode}`;
}

/** Maximum length for a nickname (6 chars from generator, but allow up to 12 for custom) */
export const NICKNAME_MAX_LENGTH = 12;
/** Minimum length for a nickname */
export const NICKNAME_MIN_LENGTH = 2;

/**
 * Validate a nickname string.
 * - Must be 2-12 characters
 * - Only alphanumeric characters allowed
 */
export function isValidNickname(nickname: string): boolean {
  if (nickname.length < NICKNAME_MIN_LENGTH || nickname.length > NICKNAME_MAX_LENGTH) {
    return false;
  }
  return /^[a-zA-Z0-9]+$/.test(nickname);
}
